import { create } from 'zustand';
import { 
	redux, 
	// devtools 
} from 'zustand/middleware';
import { ITransitionPhase } from '@r3f/transitions/spec';
import { ITrackingTypes } from '@r3f-component-data-structure';
import { Jute } from '..';

export enum ISceneActions {
	SET_TRANSITION_CATEGORY = 'SET_TRANSITION_CATEGORY',
	SET_TRANSITION_PHASE = 'SET_TRANSITION_PHASE',
	SET_IMMEDIATE_EXIT_SCENE_ID = 'SET_IMMEDIATE_EXIT__SCENE_ID',
	SET_ACTIVE_SCENE_ID = 'SET_ACTIVE_SCENE_ID',
	SET_PREVIOUS_SCENE_ID = 'SET_PREVIOUS_SCENE_ID',
	SET_TRACKING_TYPE_CHANGED = 'SET_TRACKING_TYPE_CHANGED',
	SET_PLACEMENT_MODE = 'SET_PLACEMENT_MODE',
	SET_ENABLED_TRACKING_TYPE = 'SET_ENABLED_TRACKING_TYPE',
	SET_VIDEO_TEXTURE_AVAILABLE = 'SET_VIDEO_TEXTURE_AVAILABLE'
}

interface ISetVideoTextureAvailableAction {
	type: ISceneActions.SET_VIDEO_TEXTURE_AVAILABLE;
	payload: { sceneId: string; available: boolean }
}

interface ISetActiveSceneId {
	type: ISceneActions.SET_ACTIVE_SCENE_ID;
	activeSceneId: string;
}

interface ISetPlacementMode {
	type: ISceneActions.SET_PLACEMENT_MODE;
	placementMode: boolean;
}

interface ISetEnabledTrackingType {
	type: ISceneActions.SET_ENABLED_TRACKING_TYPE;
	enabledTrackingType: ITrackingTypes | null;
}

interface ISetPreviousSceneId {
	type: ISceneActions.SET_PREVIOUS_SCENE_ID;
	previousSceneId: string;
}

interface ISetTransCatAction {
	type: ISceneActions.SET_TRANSITION_CATEGORY;
	transCatDict: ITransCatDict;
	info?: string;
}

interface ISetTransPhaseAction {
	type: ISceneActions.SET_TRANSITION_PHASE;
	transPhaseDict: ITransPhaseDict;
	info?: string;
}

interface ISetImmediateExitSceneIdAction {
	type: ISceneActions.SET_IMMEDIATE_EXIT_SCENE_ID;
	immediateExitSceneId: IImmediateExitSceneIdDict;
}

interface ISetTrackingTypeHasChangedAction {
	type: ISceneActions.SET_TRACKING_TYPE_CHANGED;
	trackingTypeHasChanged: boolean;
}

export type ISetActionUnion =
	| ISetTransCatAction
	| ISetTransPhaseAction
	| ISetImmediateExitSceneIdAction
	| ISetActiveSceneId
	| ISetPreviousSceneId
	| ISetPlacementMode
	| ISetEnabledTrackingType
	| ISetTrackingTypeHasChangedAction
	| ISetVideoTextureAvailableAction;

export enum ITransCatTypes {
	exit = 'exit',
	enter = 'enter',
	hidden = 'hidden',
}

export interface IVideoTextureAvailableById {
	[sceneId: string]: boolean;
}

export interface ITransCatDict {
	[id: string]: ITransCatTypes;
}

export interface ITransPhaseDict {
	[id: string]: ITransitionPhase;
}

export interface IImmediateExitSceneIdDict {
	[id: string]: string;
}

type ISceneState = {
	transitionCategory: ITransCatDict;
	transitionPhase: ITransPhaseDict;
	trackingTypeHasChanged: boolean;
	activeSceneId: string | null;
	placementMode: boolean;
	/**
	 * Which tracker group is live, which lags `activeSceneId` by the outgoing scene's exit transition —
	 * see `TrackerGroups`, which owns the rule. Here rather than local to that component because the
	 * preview's orbit camera has to know the world tracker has not had its turn yet: `activeSceneId`
	 * alone would have it frame and mark the scene settled before placement mode could be entered.
	 */
	enabledTrackingType: ITrackingTypes | null;
	previousSceneId: string | null;
	immediateExitSceneId: IImmediateExitSceneIdDict;
	dispatch?: (args: ISetActionUnion) => ISetActionUnion;
	videoTextureAvailableById: IVideoTextureAvailableById;
};

const initialState: ISceneState = {
	placementMode: false,
	activeSceneId: null,
	enabledTrackingType: null,
	previousSceneId: null,
	trackingTypeHasChanged: false,
	transitionCategory: {},
	transitionPhase: {},
	immediateExitSceneId: {},
	videoTextureAvailableById: {}
};

function reducer(state: ISceneState, action: ISetActionUnion): ISceneState {
	switch (action.type) {
		case ISceneActions.SET_TRANSITION_CATEGORY: {
			return {
				...state,
				transitionCategory: {
					...state.transitionCategory,
					...action.transCatDict,
				},
			};
		}
		case ISceneActions.SET_TRANSITION_PHASE: {
			const activeSceneId = state.activeSceneId;

			if (activeSceneId && action?.transPhaseDict?.[activeSceneId] && action?.transPhaseDict?.[activeSceneId] === ITransitionPhase.enterFinish) {
				Jute.logEventToAnalytics({
					category: 'Scene',
					name: ``,
					label: `${Jute.sceneTitleById[activeSceneId]} views`,
					value: 1
				});
			}
			return {
				...state,
				transitionPhase: {
					...state.transitionPhase,
					...action.transPhaseDict,
				},
			};
		}
		case ISceneActions.SET_IMMEDIATE_EXIT_SCENE_ID: {
			return {
				...state,
				immediateExitSceneId: {
					...state.immediateExitSceneId,
					...action.immediateExitSceneId,
				},
			};
		}
		case ISceneActions.SET_ACTIVE_SCENE_ID: {
			return {
				...state,
				activeSceneId: action.activeSceneId
			}
		}
		case ISceneActions.SET_PREVIOUS_SCENE_ID: {
			return {
				...state,
				previousSceneId: action.previousSceneId
			}
		}
		case ISceneActions.SET_TRACKING_TYPE_CHANGED: {
			return {
				...state,
				trackingTypeHasChanged: action.trackingTypeHasChanged
			}
		}
		case ISceneActions.SET_PLACEMENT_MODE: {
			return {
				...state,
				placementMode: action.placementMode
			}
		}
		case ISceneActions.SET_ENABLED_TRACKING_TYPE: {
			if (state.enabledTrackingType === action.enabledTrackingType) return state;
			return {
				...state,
				enabledTrackingType: action.enabledTrackingType
			}
		}

		case ISceneActions.SET_VIDEO_TEXTURE_AVAILABLE: {
			const { sceneId, available } = action.payload; 
			return {
				...state,
				videoTextureAvailableById: {
					...state.videoTextureAvailableById,
					[sceneId]: available
				}
			}
		}
	}
}

export const useSceneStore = create(redux(reducer, initialState));
// export const useSceneStore = create(devtools(redux(reducer, initialState)));
